<template>
  <v-dialog v-model="internalDialog" max-width="600px" :fullscreen="xs" scrollable>
    <div style="display:none">
      <v-dialog v-model="showPeekIntRep">
        <PeekIntRep/>
      </v-dialog>
      <v-dialog v-model="showHistory" max-width="960px" :fullscreen="xs" scrollable>
        <VerifDoneHistory
            :verif_id='verif_done.verif_id'
            :producer_id='producer_id'
            @close="showHistory = false"
            />
      </v-dialog>
    </div>
    <v-card class="vd">
      <!-- header -->
      <div class="vd__head">
        <div class="vd__head-text">
          <div class="vd__title">
            <template v-if="internalMode === 'multiple'">{{ $filters.capitalize($t('add_several')) }}</template>
            <template v-else>{{ $filters.capitalize(localized(item, 'verification_title')) }}</template>
          </div>
          <div class="vd__sub">
            <template v-if="internalMode === 'multiple'">{{ item.length }} {{ $t('verif_machines.label') }}</template>
            <template v-else>{{ localized(item, 'category_name') }} · {{ $t('verif_machines.every_n_months', { n: item.period_months }) }}</template>
          </div>
        </div>
        <v-chip v-if="internalMode !== 'multiple' && item.required_count > 1" size="small" variant="tonal" :color="item.done_count >= item.required_count ? 'success' : 'default'" label>
          {{ item.done_count }}/{{ item.required_count }}
        </v-chip>
        <v-btn icon="mdi-close" variant="text" density="comfortable" @click="closeDialog" :aria-label="$t('close')" />
      </div>

      <v-card-text class="vd__body">
        <!-- task details -->
        <div v-if="internalMode !== 'multiple'" class="vd__details">
          <p v-if="localized(item, 'indications')" class="vd__indications">{{ localized(item, 'indications') }}</p>
          <div class="vd__meta">
            <span v-if="item.last_done_date">
              <v-icon size="small" class="mr-1">mdi-calendar-check</v-icon>
              {{ $filters.capitalize($t('verif_machines.last_done')) }}: <b>{{ item.last_done_date }}</b>
            </span>
            <v-btn size="small" variant="text" color="primary" prepend-icon="mdi-history" :text="$t('verif_machines.actions.view_history')" @click="openHistory" class="ml-auto" />
          </div>
        </div>

        <!-- selected tasks in multiple mode -->
        <ol v-else class="vd__list">
          <li v-for="thing in item" :key="thing.pk">
            <span class="vd__list-title">{{ localized(thing, 'verification_title') }}</span>
            <span class="vd__list-cat">{{ localized(thing, 'category_name') }}</span>
          </li>
        </ol>

        <!-- comment kept from a previous cycle -->
        <v-alert v-if="!isNone(item.keeper_info)" type="warning" variant="tonal" density="compact" icon="mdi-pin" class="vd__kept">
          <div class="vd__kept-text">{{ item.keeper_info }}</div>
          <template v-slot:append>
            <v-btn size="small" variant="text" color="warning" :text="$filters.capitalize($t('verif_machines.actions.clear_info'))" @click="clearComment(item)" />
          </template>
        </v-alert>

        <!-- view of the last done -->
        <dl v-if="internalMode === 'view'" class="vd__facts" :class="{ 'vd__facts--kept': verif_done.keep_info_after_closure }">
          <dt>{{ $filters.capitalize($t('generic.date')) }}</dt>
          <dd>{{ $filters.formatDate(verif_done.done_at) }}</dd>
          <dt>{{ $filters.capitalize($t('intervention_reports.partner', 10)) }}</dt>
          <dd>
            <v-chip v-for="(partner, index) in verif_done.partners" :key="index" size="small" variant="tonal" class="mr-1 mb-1">{{ partner.name }}</v-chip>
          </dd>
          <dt>{{ $filters.capitalize($t('verif_machines.comment')) }}</dt>
          <dd class="vd__comment">{{ verif_done.comment }}</dd>
          <dt>{{ $filters.capitalize($t('verif_machines.keep_info_after_closure')) }}</dt>
          <dd>
            <v-icon size="small" :color="verif_done.keep_info_after_closure ? 'warning' : 'grey'">
              {{ verif_done.keep_info_after_closure ? 'mdi-pin' : 'mdi-pin-off-outline' }}
            </v-icon>
            {{ $filters.capitalize(verif_done.keep_info_after_closure ? $t('generic.yes') : $t('generic.no')) }}
          </dd>
          <dt v-if="verif_done.intervention_report_id !== null">{{ $filters.capitalize($t('intervention_reports.label', 1)) }}</dt>
          <dd v-if="verif_done.intervention_report_id !== null">
            <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-clipboard-file-outline" :text="$t('intervention_reports.label', 1)" @click="goToIntRep(verif_done.intervention_report_id)" />
          </dd>
        </dl>

        <!-- add / edit form -->
        <v-form v-else ref='form' class="vd__form">
          <v-alert v-if="internalMode === 'add' && item.done_count > 0" type="info" variant="tonal" density="compact" class="mb-3">
            {{ $filters.capitalize($t('verif_machines.last_done')) }}: {{ item.last_done_date }} ({{ item.done_count }}/{{ item.required_count }})
          </v-alert>
          <label class="vd__label">{{ $filters.capitalize($t('generic.date')) }} *</label>
          <VueDatePicker
              v-model="verif_done.done_at"
              :teleport="true"
              :enable-time-picker="false"
              :clearable="false"
              placeholder="Date"
              required
              @update:modelValue="dateError = false"
              />
          <!-- the date picker is not a Vuetify input, so the form cannot validate it: done by hand in validate() -->
          <div v-if="dateError" class="vd__error">{{ $filters.capitalize($t('generic.required')) }}</div>
          <v-autocomplete
              class="mt-3"
              v-model="verif_done.partner_ids"
              :rules="[v => !!v || $filters.capitalize($t('generic.required') )]"
              :items="$store.state.users.all"
              item-title="name"
              item-value="id"
              :label="$filters.capitalize($t('intervention_reports.partner', 10) )"
              prepend-inner-icon="mdi-account-multiple"
              variant="solo-filled"
              flat
              clearable
              chips
              closable-chips
              multiple
              >
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-title>
                    {{ $filters.capitalize($t("search_for", { noun: $t("users.label", 1) }) ) }}
                  </v-list-item-title>
                </v-list-item>
              </template>
          </v-autocomplete>
          <v-textarea
              v-model="verif_done.comment"
              :rules="[v => !!v || $filters.capitalize($t('generic.required') )]"
              :label="$filters.capitalize($t('verif_machines.comment'))"
              prepend-inner-icon="mdi-comment-text-outline"
              variant="solo-filled"
              flat
              rows="3"
              auto-grow
              />
          <v-checkbox
              v-model="verif_done.keep_info_after_closure"
              :label="$filters.capitalize($t('verif_machines.keep_info_after_closure'))"
              color="warning"
              density="compact"
              hide-details
              />
        </v-form>
      </v-card-text>

      <!-- actions -->
      <div class="vd__actions">
        <ReusableDialog
            :value='$t("todos.actions.add_item")'
            :title='$t("todos.actions.add_item")'
            :maxWidth="600"
            :withSave='true'
            v-bind:iconXS='"mdi-clipboard-check-multiple"'
            @click='openTodo'
            @save='saveTodo'
            >
            <template v-slot:content>
                <div class="dlg-context">
                  <v-icon size="small" color="primary" class="mr-1">mdi-clipboard-text-outline</v-icon>
                  <span class="text-truncate">{{ localized(item, 'verification_title') }}</span>
                </div>
                <TodoForm
                    :askProducer='false'
                    :askInterventionReport='false'
                    :askFullfilledAt='false'
                    :stacked='true'
                    >
                </TodoForm>
            </template>
        </ReusableDialog>
        <v-spacer />
        <v-btn variant="text" :text="$t('close')" @click="closeDialog" />
        <v-btn v-if="internalMode === 'view'" color="primary" variant="tonal" prepend-icon="mdi-pencil" :text="$t('actions.edit')" @click="changeToEditMode" />
        <v-btn v-else color="primary" variant="flat" prepend-icon="mdi-check" :text="$t('save')" @click="saveEntry" />
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import ReusableDialog from "@/components/ReusableDialog";
  import PeekIntRep from "@/components/PeekIntRep";
  import VerifDoneHistory from "@/components/maintenance/VerifDoneHistory";
  import TodoForm from '@/components/TodoForm';
  import { isNone, alertPopup } from "@/functions";
  import { useDisplay } from "vuetify";

  export default {
    name: 'VerifDialog',
    components: {
      ReusableDialog,
      PeekIntRep,
      TodoForm,
      VerifDoneHistory,
    },
    computed: {
      xs() {
        return useDisplay().xs.value
      },
      hasNoReport() {
        return isNone(this.verif_done.intervention_report) || isNone(this.verif_done.pk)
      },
    },
    props: {
      dialog: {
        type: Boolean,
        required: true,
      },
      item: {
        type: Object,
        required: true,
      },
      mode: {
        type: String,
        required: true,
      },
      producer_id: {},
    },
    data() {
      return {
        internalDialog: this.dialog,
        verif_done: this.$store.state.verif_dones.struct,
        showPeekIntRep: false,
        showHistory: false,
        internalMode: undefined,
        dateError: false,
      };
    },
    watch: {
      dialog(val) {
        this.internalDialog = val;
      },
      internalDialog(val) {
        this.$emit('update:dialog', val);
      },
      item(newItem) {
        // add mode always starts a new done, even if the task already has one this cycle
        if (newItem.last_done_id && this.mode !== 'add') {
          this.$store.dispatch('verif_dones/getItem', newItem.last_done_id).then((v_d)=> {
            this.verif_done = v_d
          }).catch((e)=>{
            this.$store.dispatch('snackbar/showError', e)})
        } else { 
          this.verif_done = this.freshDone(newItem)
        }
      },
    },
    methods: {
      isNone,
      closeDialog() {
        this.internalDialog = false;
      },
      openHistory() {
        this.showHistory = true;
      },
      async saveEntry() {
        const isValid = await this.validate();

        if (!isValid) return
        this.$emit('save', this.verif_done);
        this.closeDialog();
      },
      async validate () {
        const { valid } = await this.$refs.form.validate()
        this.dateError = !this.verif_done.done_at
        return valid && !this.dateError
      },
      goToIntRep(pk) {
        this.$store.dispatch('intervention_reports/getItem', pk).then(()=>{
          this.showPeekIntRep = true;
        })
      },
      openTodo() {
        let prefilled = this.$store.state.todos.struct
        prefilled.producer_id = this.producer_id
        prefilled.producer = this.$store.state.producers.all.find(a => a.pk==this.producer_id)
        // the todo is about this check: start from its title
        if (this.internalMode !== 'multiple') prefilled.description = this.localized(this.item, 'verification_title')
        this.$store.dispatch("todos/updateEdit", prefilled)
      },
      saveTodo(){
        this.$emit('saveTodo')
      },
      clearComment(item){
        const payload = {
          producer_id: this.producer_id,
          verif_machine: item.pk
        }
        alertPopup(this.$t('verif_machines.clear_comment_confirm')).then((result)=>{
          if(result) {
            this.$store.dispatch('verif_dones/clearComment', payload).then(()=>{
              this.$emit('clearKeeperInfo', this.item)
            })
          }
        })
      },
      changeToEditMode(){
        this.internalMode = "edit"
      },
      localized(item, field) {
        if (this.$store.getters.language === 'en' && item[field + '_en']) return item[field + '_en']
        return item[field]
      },
      freshDone(item) {
        return {
          ...this.$store.state.verif_dones.struct,
          done_at: new Date(),
          partner_ids: [this.$store.state.user.id],
          verif: item,
          verif_id: item.pk,
          producer_id: this.producer_id
        }
      },
    },
    //  computed(){
    //    availablePartners() {
    //      //TODO: filter by users in stockage -> p-station -> producer?
    //      return store.state.users.filter(
    //    },
    //  },
    mounted(){
      this.internalMode = this.mode
      if (this.$store.state.users.all.length <= 0 && !this.$store.state.users.isLoading) {
        this.$store.dispatch('users/getAll')
      }
    },
    updated() {
      this.internalMode = this.mode
    },
  };
</script>
<style scoped>
.vd {
  display: flex;
  flex-direction: column;
}
.vd__head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 8px 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.vd__head-text {
  flex: 1 1 auto;
  min-width: 0;
}
.vd__title {
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.25;
  overflow-wrap: anywhere;
}
.vd__sub {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}
.vd__body {
  padding: 12px 16px;
}
.vd__details {
  margin-bottom: 12px;
}
.vd__indications {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
  background: #f6f7f9;
  border-radius: 8px;
  padding: 8px 10px;
  margin: 0 0 6px;
  white-space: pre-wrap;
}
.vd__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
}
.vd__list {
  margin: 0 0 12px;
  padding-left: 20px;
  font-size: 0.9rem;
}
.vd__list li {
  padding: 3px 0;
}
.vd__list-title {
  font-weight: 500;
}
.vd__list-cat {
  margin-left: 6px;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.55);
}
.vd__kept {
  margin-bottom: 12px;
}
.vd__kept-text {
  white-space: pre-wrap;
}
.vd__facts {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 16px;
  margin: 0;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.9rem;
}
.vd__facts--kept {
  background: #fff8e1;
  border-color: #ffe082;
}
.vd__facts dt {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
  padding-top: 2px;
}
.vd__facts dd {
  margin: 0;
  min-width: 0;
}
.vd__comment {
  white-space: pre-wrap;
}
.vd__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 4px;
}
.dlg-context {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  background: #f6f7f9;
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 10px;
  min-width: 0;
}
.vd__error {
  color: #b00020;
  font-size: 0.75rem;
  margin: 4px 0 0 4px;
}
.vd__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
@media (max-width: 599px) {
  .vd__facts {
    grid-template-columns: 1fr;
    gap: 2px;
  }
  .vd__facts dt {
    margin-top: 8px;
  }
}
</style>
